<template>
    <div>
        <edit-rule-table
            v-if="editable"
            v-bind:columns="columns"
            v-bind:empty-rule-table-message="getEmptyRuleTableMessage(ruleType)"
            v-bind:rule-type="ruleType" />
        <pc-table
            v-else
            v-bind:items="rulesForTable"
            v-bind:columns="columns"
            v-bind:empty-text="getEmptyRuleTableMessage(ruleType)"
            v-bind:no-card-view="noCardView"
            hide-search
            key-prop=""
            v-on:page-changed="$emit('page-changed')">
            <template v-slot:Status="row">
                <pc-bs-icon
                    v-bind:name="getStatusIcon(row)"
                    style="min-width: 1rem" />
            </template>
        </pc-table>
    </div>
</template>
<script>
    import { KEYS, NAMESPACE } from "./i18n/automationRuleTable.i18n";
    import { mapI18n } from "Util/i18n";
    import editRuleTable from "Automations/editAutomation/editRuleTable.vue";
    import { PROGRAM_DATA_TYPES } from "Automations/programData/programDataStore";
    import { getFrequencyText, getConditionText, getContinueProcessingText } from "./ruleTextI18nHelper";
    import { getRuleColumnVisibility } from "./ruleColumnVisHelper";
    import { RULE_TYPE } from "Types/automationTypes";

    const STOP_INFO_FLAG = "_STOP_INFO_FLAG_";

    export default {
        components: { editRuleTable },
        props: {
            ruleType: { type: String, required: true },
            rules: { type: Array, required: false, default: null },
            editable: { type: Boolean, required: false, default: false },
            noCardView: { type: Boolean, required: false, default: false },
            showStatusColumn: { type: Boolean, required: false, default: false }
        },
        emits: ["page-changed"],
        computed: {
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            ),
            rulesForTable() {
                const updatedRules = [...this.rules];
                // Find the index of the rule where processing was *stopped*, i.e., the first row where WasTaken = true and
                //  StopFurtherProcessing = true, not where it just *ended* because it ran out of rules to process.
                const automationStoppedIndex = this.rules.findIndex(
                    (rule) => rule.WasTaken && rule.StopFurtherProcessing
                );

                // If the matching row is found before the last rule, insert a new row after it to mark where processing stopped
                if (automationStoppedIndex !== -1 && automationStoppedIndex < this.rules.length - 1) {
                    const stopInfoRow = {
                        Order: -1,
                        FormulaId: STOP_INFO_FLAG,
                        Action: null,
                        WasSatisfied: false,
                        WasTaken: false,
                        StopFurtherProcessing: null,
                        HasExecutionError: false,
                        ErrorMessage: ""
                    };

                    // Insert the new row after the selected index
                    updatedRules.splice(automationStoppedIndex + 1, 0, stopInfoRow);
                }
                // Return the (possibly) modified array
                return updatedRules;
            },
            columns() {
                const colVis = getRuleColumnVisibility(this.ruleType);
                return [
                    {
                        header: " ",
                        property: "Status",
                        visible: this.showStatusColumn
                    },
                    {
                        header: this[KEYS.orderColumnHeader],
                        property: "Order",
                        visible: colVis.showOrder,
                        displayValue: this.getOrderText
                    },
                    {
                        header: this[KEYS.frequencyRuleColumnHeader],
                        property: "Frequency",
                        visible: colVis.showFrequency,
                        displayValue: this.getFrequencyText
                    },
                    {
                        header: this[KEYS.conditionRuleColumnHeader],
                        property: "FormulaId",
                        visible: colVis.showCondition,
                        displayValue: this.getConditionText
                    },
                    {
                        header: this[KEYS.errorMessageRuleColumnHeader],
                        property: "ErrorMessage",
                        visible: colVis.showErrorMessage
                    },
                    {
                        header: this[KEYS.actionRuleColumnHeader],
                        property: "Action",
                        visible: colVis.showAction,
                        displayValue: this.getActionText
                    },
                    {
                        header: this[KEYS.continueProcessingRuleColumnHeader],
                        property: "StopFurtherProcessing",
                        visible: colVis.showAction,
                        displayValue: this.getContinueProcessingText
                    }
                ];
            }
        },
        methods: {
            getOrderText(order) {
                if (order === -1) {
                    return "";
                }
                return order + 1; //display is 1 indexed
            },
            getFrequencyText(frequency) {
                if (frequency) {
                    return getFrequencyText(this.$app, frequency);
                }
                return "";
            },
            getConditionText(formulaId) {
                if (formulaId === STOP_INFO_FLAG) {
                    return this.stopInfoConditionMessage;
                }
                const formula = this.$store.getters[PROGRAM_DATA_TYPES.getters.formulasById][formulaId];
                return getConditionText(this.$app, formula);
            },
            getActionText(action) {
                if (action === null) {
                    return this.stopInfoActionMessage;
                }
                return this.$store.getters[PROGRAM_DATA_TYPES.getters.getActionSummary](action);
            },
            getContinueProcessingText(stopFurtherProcessing) {
                if (stopFurtherProcessing === null) {
                    return "";
                }
                return getContinueProcessingText(this.$app, stopFurtherProcessing);
            },
            getEmptyRuleTableMessage(ruleType) {
                switch (ruleType) {
                    case RULE_TYPE.programWideProject:
                        return this.noProgramWideActionsMessage;
                    case RULE_TYPE.action:
                        return this.noActionsMessage;
                    case RULE_TYPE.validation:
                        return this.noValidationsMessage;
                    case RULE_TYPE.program:
                        return this.noProgramActionsMessage;
                    default:
                        return null;
                }
            },
            getStatusIcon(rule) {
                if (rule.FormulaId === STOP_INFO_FLAG) {
                    return "stop-circle";
                } else if (rule.HasExecutionError) {
                    return "x-circle";
                } else if (rule.WasSatisfied && !rule.WasTaken) {
                    return "exclamation-circle";
                } else if (rule.WasSatisfied) {
                    return "check-circle";
                }
            }
        }
    };
</script>
